import { defineRenderNode, type RenderNodeDefsSubset } from './renderNodeDefs.js';

// Graph availability (which kinds double as static shape parameters) is
// injected from the engine's GEOMETRY_NODE_KINDS in renderNodeDefs.ts.

export const VECTOR_NODE_DEFS: RenderNodeDefsSubset<
  | 'Vec2'
  | 'Vec3'
  | 'Vec4'
  | 'SplitVec2'
  | 'Length'
  | 'SplitVec3'
  | 'Dot'
  | 'Cross'
  | 'Normalize'
  | 'AddVectors'
  | 'SubtractVectors'
  | 'ScaleVector'
  | 'Illuminance'
> = {
  Vec2: defineRenderNode({
    label: 'Vec2', cat: 'vector',
    inputs: [{ name: 'x', type: 'float' }, { name: 'y', type: 'float' }],
    outputs: [{ name: 'v', type: 'vec2' }],
  }),
  Vec3: defineRenderNode({
    label: 'Vec3', cat: 'vector',
    inputs: [{ name: 'r', type: 'float' }, { name: 'g', type: 'float' }, { name: 'b', type: 'float' }],
    outputs: [{ name: 'v', type: 'vec3' }],
  }),
  Vec4: defineRenderNode({
    label: 'Vec4', cat: 'vector',
    inputs: [
      { name: 'r', type: 'float' },
      { name: 'g', type: 'float' },
      { name: 'b', type: 'float' },
      { name: 'a', type: 'float' }
    ],
    outputs: [{ name: 'v', type: 'vec4' }],
  }),
  SplitVec2: defineRenderNode({
    label: 'Split Vec2', cat: 'vector',
    inputs: [{ name: 'v', type: 'vec2' }],
    outputs: [{ name: 'x', type: 'float' }, { name: 'y', type: 'float' }],
  }),
  Length: defineRenderNode({
    label: 'Length', cat: 'vector',
    inputs: [{ name: 'v', label: 'Vector', type: 'vec2' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  // Three-vector arithmetic (crates/graph/src/nodes.rs): a world point, a
  // normal, a direction to a lamp, each one value rather than three scalars.
  SplitVec3: defineRenderNode({
    label: 'Split Vec3', cat: 'vector',
    inputs: [{ name: 'v', type: 'vec3' }],
    outputs: [{ name: 'x', type: 'float' }, { name: 'y', type: 'float' }, { name: 'z', type: 'float' }],
  }),
  Dot: defineRenderNode({
    label: 'Dot', cat: 'vector',
    inputs: [{ name: 'a', label: 'A', type: 'vec3' }, { name: 'b', label: 'B', type: 'vec3' }],
    outputs: [{ name: 'result', label: 'Result', type: 'float' }],
  }),
  Cross: defineRenderNode({
    label: 'Cross', cat: 'vector',
    inputs: [{ name: 'a', label: 'A', type: 'vec3' }, { name: 'b', label: 'B', type: 'vec3' }],
    outputs: [{ name: 'v', type: 'vec3' }],
  }),
  Normalize: defineRenderNode({
    label: 'Normalize', cat: 'vector',
    inputs: [{ name: 'v', label: 'Vector', type: 'vec3' }],
    outputs: [{ name: 'v', type: 'vec3' }],
  }),
  AddVectors: defineRenderNode({
    label: 'Add Vectors', cat: 'vector',
    inputs: [{ name: 'a', label: 'A', type: 'vec3' }, { name: 'b', label: 'B', type: 'vec3' }],
    outputs: [{ name: 'v', type: 'vec3' }],
  }),
  SubtractVectors: defineRenderNode({
    label: 'Subtract Vectors', cat: 'vector',
    inputs: [{ name: 'a', label: 'A', type: 'vec3' }, { name: 'b', label: 'B', type: 'vec3' }],
    outputs: [{ name: 'v', type: 'vec3' }],
  }),
  ScaleVector: defineRenderNode({
    label: 'Scale Vector', cat: 'vector',
    inputs: [
      { name: 'v', label: 'Vector', type: 'vec3' },
      { name: 'scale', label: 'Scale', type: 'float', range: { min: -100, max: 100, step: 0.01, subtype: 'factor' } },
    ],
    outputs: [{ name: 'v', type: 'vec3' }],
  }),
  // What the document's lamps give a point with a normal (crates/graph/src/
  // light.rs): the lamps are the document's `lights`, never the material's.
  Illuminance: defineRenderNode({
    label: 'Illuminance', cat: 'vector',
    inputs: [{ name: 'point', label: 'Point', type: 'vec3' }, { name: 'normal', label: 'Normal', type: 'vec3' }],
    outputs: [{ name: 'light', label: 'Light', type: 'vec3' }],
  }),
};
